// Lib
import { useCallback, useEffect } from 'react';
import { Editor } from '@tiptap/react';
import { useSelector } from 'react-redux';

// Selectors
import { getPlatformDetailsSelector } from '../../platform/platformSelector';
import { userLocalePreferenceSelector } from '../../user/currentUserSelector';

// Actions
import { registerTiptapEditor, setActiveTiptapEditor, unregisterTiptapEditor } from './store/tiptapEditorStoreActions';

// Hooks
import useElementTiptapEditor, {
    UseTiptapEditorProps,
    UseTiptapEditorReturn,
} from '../../../common/tiptap/useElementTiptapEditor';
import { useTiptapEditorDispatch } from './store/tiptapEditorStoreHooks';
import { getQuotationCssCustomProperties } from '../../../common/tiptap/utils/quotationCssCustomProperties';

/**
 * Dispatches an editor update to the Tiptap editor redux store.
 */
const useDispatchTiptapActiveEditorStoreUpdate = () => {
    const dispatch = useTiptapEditorDispatch();

    return useCallback(
        (editor: Editor | null) => {
            dispatch(setActiveTiptapEditor(editor));
        },
        [dispatch],
    );
};

/**
 * Configures the Tiptap editor for use in the client.
 *
 * Thin wrapper around useElementTiptapEditor, which is in the common module and so can't include client code.
 */
export const useClientElementTiptapEditor = (
    elementTiptapEditorArgs: Omit<UseTiptapEditorProps, 'updateActiveEditorStore' | 'platformDetails'>,
): UseTiptapEditorReturn => {
    const platformDetails = useSelector(getPlatformDetailsSelector);
    const localePreference = useSelector(userLocalePreferenceSelector);
    const quotationCssCustomProperties = getQuotationCssCustomProperties(localePreference);
    const dispatch = useTiptapEditorDispatch();
    const updateActiveEditorStore = useDispatchTiptapActiveEditorStoreUpdate();
    const editorResult = useElementTiptapEditor({
        ...elementTiptapEditorArgs,
        updateActiveEditorStore,
        platformDetails,
        quotationCssCustomProperties,
    });
    const { editor } = editorResult;
    const { registryEditorId } = elementTiptapEditorArgs;

    useEffect(() => {
        if (!editor || !registryEditorId) return;

        dispatch(registerTiptapEditor(registryEditorId, editor));
        return () => {
            dispatch(unregisterTiptapEditor(registryEditorId, editor));
        };
    }, [dispatch, editor, registryEditorId]);

    return editorResult;
};

export default useClientElementTiptapEditor;
